import * as React from 'react';
import { IHasTestData } from '../../test-utilities/IHasTestData';
import { eBrandImageSizes, LoadingImage } from '../BrandImages';
import { Button } from '../Button';
import { Grid } from '../Grid';
import { Icon, IconSize, IconType } from '../Icon';
import { ModalWindow } from '../ModalWindow';
import { BodyText, Header3Text } from '../Typography';
import { FlexRow } from '../FlexRow';
import styled from 'styled-components';
import { ModalWindowTypes } from '../ModalWindow';
import { mergeTests } from '../../test-utilities/mergeTests';

type DialogWindowType = 'processing' | 'success' | 'error';

const DialogIcon: React.FC<{ iconType: DialogWindowType }> = ({ iconType }) =>
  iconType === 'processing' ? (
    <LoadingImage imageSize={eBrandImageSizes.Small} />
  ) : iconType === 'success' ? (
    <Icon type={IconType.Party} size={IconSize.Large} />
  ) : iconType === 'error' ? (
    <Icon type={IconType.Alert} size={IconSize.Large} />
  ) : null;

export const StyledDialogIconDiv = styled.div`
  display: flex;
  justify-content: center;
`;

export interface IDialogWindowProps extends IHasTestData {
  type: DialogWindowType;
  title?: string | React.ReactNode;
  message?: string | React.ReactNode;
  icon?: React.ReactNode;
  onClose?: () => void;
  onRetry?: () => void;
  autoFocus?: 'close' | 'retry';
  modalUiType?: ModalWindowTypes /* allows for adjustment when V2 theme provider causes style issues */;
}

export const DialogWindow: React.FC<IDialogWindowProps> = ({
  message,
  onClose,
  onRetry,
  title,
  type,
  'data-test-id': dataTestID,
  autoFocus,
  icon,
  modalUiType,
}) => (
  <ModalWindow
    minHeight={type === 'processing' ? 'auto' : undefined}
    mode={type === 'processing' ? 'transition' : 'default'}
    onClose={onClose}
    uitype={modalUiType ? modalUiType : 'medium'}
  >
    <Grid textAlign={'center'}>
      <StyledDialogIconDiv>{icon ? icon : <DialogIcon iconType={type} />}</StyledDialogIconDiv>
      {typeof title === 'string' ? <Header3Text>{title}</Header3Text> : title}
      {typeof message === 'string' ? <BodyText>{message}</BodyText> : message}
      {type !== 'processing' && (
        <FlexRow justifyContent={'center'} verticalAlign={'center'}>
          {type === 'error' && onRetry ? (
            <Button
              type={'button'}
              data-test-id={mergeTests(dataTestID, 'retry')}
              intent={'primary'}
              onClick={onRetry}
              autoFocus={autoFocus === 'retry'}
            >
              Retry
            </Button>
          ) : null}
          <Button
            type={'button'}
            data-test-id={mergeTests(dataTestID, 'close')}
            intent={'ghost'}
            isInline
            onClick={onClose}
            autoFocus={autoFocus === 'close'}
          >
            Close
          </Button>
        </FlexRow>
      )}
    </Grid>
  </ModalWindow>
);
